import api from "../../../api.js";

// Search RX numbers/scripts by name or number
export async function searchRxNumbers(searchTerm) {
  try {
    const response = await api.get("/api/label/rx-numbers/search", {
      params: { search: searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching RX numbers:', error);
    throw error;
  }
}

// Search patients by name
export async function searchPatients(searchTerm) {
  try {
    const response = await api.get("/api/label/patients/search", {
      params: { search: searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching patients:', error);
    throw error;
  }
}

// Get RX numbers for a specific patient
export async function getPatientRxNumbers(patientId) {
  try {
    const response = await api.get("/api/label/rx-numbers", {
      params: { patientId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting patient RX numbers:', error);
    throw error;
  }
}

// Get medications for a specific prescription
export async function getPrescriptionMedications(prescriptionId) {
  try {
    const response = await api.get("/api/label/medications", {
      params: { prescriptionId: prescriptionId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting prescription medications:', error);
    throw error;
  }
}

// Search inventory table for medication using existing API
export async function searchInventory(medicationName) {
  try {
    console.log(`Searching inventory for medication: "${medicationName}"`);
    
    // Use existing inventory API to search for medication
    // We'll search all departments and filter by medication name
    const departmentsResponse = await api.get("/api/departments");
    
    if (departmentsResponse.data?.data) {
      console.log(`Found ${departmentsResponse.data.data.length} departments to search`);
      
      for (const department of departmentsResponse.data.data) {
        // Use sectionid as the department ID
        const deptId = department.sectionid;
        
        if (deptId) {
          try {
            const inventoryResponse = await api.get(`/api/inventory/department/${deptId}`);
            
            if (inventoryResponse.data?.data) {
              console.log(`Searching department ${deptId} with ${inventoryResponse.data.data.length} items`);
              
              // Log all available medications for debugging
              const medicationNames = inventoryResponse.data.data.map(item => item.name);
              console.log(`Available medications in department ${deptId}:`, medicationNames);
              
              // More restrictive search - only match actual medications
              const foundItem = inventoryResponse.data.data.find(
                item => {
                  if (!item.name) return false;
                  
                  const itemName = item.name.toLowerCase().trim();
                  const searchName = medicationName.toLowerCase().trim();
                  
                  // Skip obvious non-medical items (be less restrictive)
                  const nonMedicalKeywords = ['cookware', 'utensil', 'plate', 'bowl', 'cup', 'spoon', 'fork', 'knife', 'notebook'];
                  if (nonMedicalKeywords.some(keyword => itemName.toLowerCase().includes(keyword))) {
                    console.log(`Skipping non-medical item: "${item.name}"`);
                    return false;
                  }
                  
                  // Exact match - highest priority
                  if (itemName === searchName) {
                    console.log(`Exact match found: "${item.name}"`);
                    return true;
                  }
                  
                  // Check if the main medication name matches (first word, must be longer than 3 chars)
                  const searchMainName = searchName.split(/\s+/)[0]; // e.g., "Panado" from "Panado Extra"
                  const itemMainName = itemName.split(/\s+/)[0];
                  
                  if (searchMainName.length > 3 && itemMainName === searchMainName) {
                    console.log(`Main medication name match: "${item.name}" (matched "${itemMainName}" with "${searchMainName}")`);
                    return true;
                  }
                  
                  // Contains match with main medication name (both ways)
                  if (searchMainName.length > 3 && (itemName.toLowerCase().includes(searchMainName.toLowerCase()) || searchMainName.toLowerCase().includes(itemMainName.toLowerCase()))) {
                    console.log(`Main name contains match: "${item.name}"`);
                    return true;
                  }
                  
                  // General contains match (but be more restrictive)
                  if (itemName.toLowerCase().includes(searchName.toLowerCase()) || searchName.toLowerCase().includes(itemName.toLowerCase())) {
                    console.log(`General contains match: "${item.name}"`);
                    return true;
                  }
                  
                  return false;
                }
              );
              
              if (foundItem) {
                console.log(`Found inventory item:`, foundItem);
                return { success: true, data: [foundItem] };
              }
            }
          } catch (error) {
            console.log(`Error searching department ${deptId}:`, error.message);
            // Continue searching other departments
          }
        }
      }
    }
    
    console.log(`No inventory match found for "${medicationName}"`);
    return { success: false, data: [] };
  } catch (error) {
    console.error('Error in searchInventory:', error);
    return { success: false, data: [] };
  }
}

// Search item table by SKU using existing API
export async function searchItemBySku(sku) {
  try {
    // Use existing inventory API to get item by SKU
    const response = await api.get(`/api/inventory/item/${encodeURIComponent(sku)}`);
    
    if (response.data?.data) {
      const itemData = response.data.data;
      
      // Check if item exists AND has meaningful data (not just empty object)
      if (itemData.item && 
          itemData.item.category && 
          itemData.item.category.trim() !== '') {
        return { success: true, data: [itemData] };
      }
    }
    return { success: false, data: [] };
  } catch (error) {
    return { success: false, data: [] };
  }
}

// Check if medication exists in item table by name
export async function checkMedicationInItemTable(medicationName) {
  console.log('checkMedicationInItemTable called with:', medicationName);
  try {
    // Search for medication in inventory to get SKU
    const inventoryResponse = await searchInventory(medicationName);
    console.log('Inventory search response:', inventoryResponse);
    
    if (inventoryResponse.success && inventoryResponse.data && inventoryResponse.data.length > 0) {
      const medication = inventoryResponse.data[0];
      console.log('Found medication in inventory:', medication);
      
      // Check if this SKU exists in the item table
      const itemResponse = await searchItemBySku(medication.sku);
      console.log('Item table check response:', itemResponse);
      
      const result = {
        success: true,
        isInItemTable: itemResponse.success && itemResponse.data.length > 0,
        sku: medication.sku
      };
      console.log('Final result:', result);
      return result;
    }
    
    console.log('Medication not found in inventory');
    return { success: false, isInItemTable: false, sku: null };
  } catch (error) {
    console.error('Error checking medication in item table:', error);
    return { success: false, isInItemTable: false, sku: null };
  }
}

// Get drug information by SKU
export async function getDrugBySku(sku) {
  try {
    const response = await api.get(`/api/label/drugs/sku/${encodeURIComponent(sku)}`);
    if (response.data?.data) {
      return { success: true, data: response.data.data };
    }
    return { success: false, data: null };
  } catch (error) {
    console.error('Error fetching drug by SKU:', error);
    return { success: false, data: null };
  }
}

// Get strength by ID
export async function getStrengthById(strengthId) {
  try {
    const response = await api.get(`/api/label/strengths/${strengthId}`);
    if (response.data?.data) {
      return { success: true, data: response.data.data };
    }
    return { success: false, data: null };
  } catch (error) {
    console.error('Error fetching strength by ID:', error);
    return { success: false, data: null };
  }
}

// Get dosage form by ID
export async function getDosageFormById(dosageFormId) {
  try {
    const response = await api.get(`/api/label/dosageforms/${dosageFormId}`);
    if (response.data?.data) {
      return { success: true, data: response.data.data };
    }
    return { success: false, data: null };
  } catch (error) {
    console.error('Error fetching dosage form by ID:', error);
    return { success: false, data: null };
  }
}

// Get patient by ID
export async function getPatientById(patientId) {
  try {
    const response = await api.get(`/api/label/patients/${patientId}`);
    if (response.data?.data) {
      return { success: true, data: response.data.data };
    }
    return { success: false, data: null };
  } catch (error) {
    console.error('Error fetching patient by ID:', error);
    return { success: false, data: null };
  }
}

// Get cost price by SKU from inventory
export async function getCostPriceBySku(sku) {
  try {
    console.log(`Fetching cost price for SKU: ${sku}`);
    
    // First get departments to find which one contains this SKU
    const departmentsResponse = await api.get("/api/departments");
    
    if (departmentsResponse.data?.data) {
      for (const department of departmentsResponse.data.data) {
        const deptId = department.sectionid;
        
        if (deptId) {
          try {
            const inventoryResponse = await api.get(`/api/inventory/department/${deptId}`);
            
            if (inventoryResponse.data?.data) {
              const item = inventoryResponse.data.data.find(
                item => item.sku === sku
              );
              
              if (item) {
                console.log(`Found cost price for SKU ${sku}:`, item.costprice);
                return { 
                  success: true, 
                  costPrice: item.costprice,
                  data: item
                };
              }
            }
          } catch (error) {
            console.log(`Error searching department ${deptId} for SKU ${sku}:`, error.message);
          }
        }
      }
    }
    
    console.log(`No cost price found for SKU ${sku}`);
    return { success: false, costPrice: null };
  } catch (error) {
    console.error('Error fetching cost price by SKU:', error);
    return { success: false, costPrice: null };
  }
}